import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { theme } from '../utils/theme';
import { API_BASE_URL } from '../utils/ApiManager';

// Move styles outside component to avoid re-creation on each render
const spinAnimationStyles = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  @keyframes spin-reverse {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(-360deg); }
  }
`;

// Add styles to document head once
if (!document.getElementById('card-animations')) {
  const styleSheet = document.createElement("style");
  styleSheet.id = 'card-animations';
  styleSheet.textContent = spinAnimationStyles;
  document.head.appendChild(styleSheet);
}

const Card = ({ id, image, prompt, status, action, variety, processedVideos = [], onDelete, onVideoClick }) => {
  const { t } = useTranslation('card');
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleDelete = () => {
    if (window.confirm(t('deleteConfirm'))) {
      onDelete?.(id);
    }
  };

  const getImageUrl = (preview) => {
    // Handle blob URLs (for newly selected files)
    if (preview?.startsWith('blob:')) return preview;
    
    // Handle secure URLs (from the new system)
    if (preview?.startsWith('/api/files/access')) {
      return `${API_BASE_URL}${preview}`;
    }
    
    // Handle absolute paths (starting with /uploads/) - legacy support during transition
    if (preview?.startsWith('/uploads/')) {
      return `${API_BASE_URL}${preview}`;
    }
    
    // Handle relative paths (just filename, without /uploads/) - legacy support
    if (preview && !preview.includes('/')) {
      return `${API_BASE_URL}/uploads/${preview}`;
    }

    // Default case: return the preview as is
    return preview;
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(true); // Consider it "loaded" to remove loading animation
  };

  const BucketIcon = () => (
    <svg 
      fill="currentColor" 
      viewBox="0 0 487.1 487.1" 
      xmlns="http://www.w3.org/2000/svg"
    >
      <g>
        <path d="M342.3,137.978H385l-63.3-108.6c-5.1-8.8-16.4-11.8-25.2-6.6c-8.8,5.1-11.8,16.4-6.6,25.2L342.3,137.978z" />
        <path d="M197.4,47.978c5.1-8.8,2.2-20.1-6.6-25.2s-20.1-2.2-25.2,6.6l-63.3,108.7H145L197.4,47.978z" />
        <path d="M455.7,171.278H31.3c-17.3,0-31.3,14-31.3,31.3v34.7c0,17.3,14,31.3,31.3,31.3h9.8l30.2,163.7 c3.8,19.3,21.4,34.6,39.7,34.6h12h78.8c8,0,18.4,0,29,0l0,0h9.6h9.6l0,0c10.6,0,21,0,29,0h78.8h12c18.3,0,35.9-15.3,39.7-34.6 l30.4-163.8h15.9c17.3,0,31.3-14,31.3-31.3v-34.7C487,185.278,473,171.278,455.7,171.278z M172.8,334.878v70.6 c0,10.1-8.2,17.7-17.7,17.7c-10.1,0-17.7-8.2-17.7-17.7v-29.6v-69.4c0-10.1,8.2-17.7,17.7-17.7c10.1,0,17.7,8.2,17.7,17.7V334.878 z M229.6,334.878v70.6c0,10.1-8.2,17.7-17.7,17.7c-10.1,0-17.7-8.2-17.7-17.7v-29.6v-69.4c0-10.1,8.2-17.7,17.7-17.7 s17.7,8.2,17.7,17.7V334.878z M286.7,375.878v29.6c0,9.5-7.6,17.7-17.7,17.7c-9.5,0-17.7-7.6-17.7-17.7v-70.6v-28.4 c0-9.5,8.2-17.7,17.7-17.7s17.7,7.6,17.7,17.7V375.878z M343.5,375.878v29.6c0,9.5-7.6,17.7-17.7,17.7c-9.5,0-17.7-7.6-17.7-17.7 v-70.6v-28.4c0-9.5,7.6-17.7,17.7-17.7c9.5,0,17.7,7.6,17.7,17.7V375.878z" />
      </g>
    </svg>
  );

  return (
    <div className={theme.card.base + ' bg-opacity-75 w-72 h-72 relative overflow-hidden group'}>
      {/* Loading Animation - Only shown while image is loading */}
      {!imageLoaded && (
        <div className="absolute inset-0 w-full h-full flex items-center justify-center bg-gray-100">
          <div className="flex flex-col items-center">
            <svg className="animate-spin w-12 h-12 text-[#6D9773]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="mt-2 text-sm text-[#0C3B2E] font-medium">{t('loading', 'Loading...')}</span>
          </div>
        </div>
      )}

      {/* Error Display */}
      {imageError && (
        <div className="absolute inset-0 w-full h-full flex items-center justify-center bg-gray-100">
          <div className="flex flex-col items-center p-4 text-center">
            <svg className="w-12 h-12 text-[#FFBA00]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span className="mt-2 text-sm text-[#0C3B2E] font-medium">{t('imageError', 'Image could not be loaded')}</span>
          </div>
        </div>
      )}

      {/* Processing Overlay - Only shown when status is processing */}
      {status === 'processing' && imageLoaded && !imageError && (
        <div className="absolute inset-0 w-full h-full bg-black/50 backdrop-blur-sm flex flex-col items-center justify-center z-10">
          <div className="relative">
            {/* Large Main Gear - Always animating */}
            <motion.svg
              className="w-20 h-20 text-white"
              viewBox="0 0 24 24"
              fill="currentColor"
              stroke="none"
              animate={{ rotate: 360 }}
              transition={{ duration: 4, repeat: Infinity, ease: "linear", repeatDelay: 0 }}
            >
              <path d="M12 2c-0.3 0-0.5 0.2-0.7 0.4L10 4.1c-0.5 0.1-1 0.3-1.5 0.5L7.2 3.7c-0.2-0.2-0.5-0.2-0.8-0.1l-2 1.2C4.2 5 4.1 5.3 4.2 5.6l0.6 2.2c-0.3 0.4-0.5 0.9-0.7 1.3L2 9.7c-0.3 0.1-0.5 0.3-0.5 0.6v2.4c0 0.3 0.2 0.5 0.5 0.6l2.1 0.7c0.2 0.5 0.4 0.9 0.7 1.3l-0.6 2.2c-0.1 0.3 0 0.6 0.2 0.7l2 1.2c0.2 0.1 0.5 0.1 0.8-0.1l1.3-1.1c0.5 0.2 1 0.4 1.5 0.5l0.7 2.1c0.1 0.3 0.3 0.4 0.6 0.4h2.4c0.3 0 0.5-0.2 0.7-0.4l0.7-2.1c0.5-0.1 1-0.3 1.5-0.5l1.3 1.1c0.2 0.2 0.5 0.2 0.8 0.1l2-1.2c0.3-0.2 0.4-0.5 0.2-0.7l-0.6-2.2c0.3-0.4 0.5-0.9 0.7-1.3L22 13.3c0.3-0.1 0.5-0.3 0.5-0.6v-2.4c0-0.3-0.2-0.5-0.5-0.6l-2.1-0.7c-0.2-0.5-0.4-0.9-0.7-1.3l0.6-2.2c0.1-0.3 0-0.6-0.2-0.7l-2-1.2C17.3 3.4 17 3.5 16.8 3.7l-1.3 1.1c-0.5-0.2-1-0.4-1.5-0.5L13.3 2.4C13.2 2.2 13 2 12.7 2H12zM12 8c2.2 0 4 1.8 4 4s-1.8 4-4 4s-4-1.8-4-4s1.8-4 4-4z"/>
            </motion.svg>

            {/* Medium Gear - Always animating */}
            <motion.svg
              className="w-16 h-16 text-[#FFBA00]/90 absolute -right-6 top-0"
              viewBox="0 0 24 24"
              fill="currentColor"
              stroke="none"
              animate={{ rotate: -360 }}
              transition={{ duration: 4, repeat: Infinity, ease: "linear", repeatDelay: 0 }}
            >
              <path d="M12 2c-0.3 0-0.5 0.2-0.7 0.4L10 4.1c-0.5 0.1-1 0.3-1.5 0.5L7.2 3.7c-0.2-0.2-0.5-0.2-0.8-0.1l-2 1.2C4.2 5 4.1 5.3 4.2 5.6l0.6 2.2c-0.3 0.4-0.5 0.9-0.7 1.3L2 9.7c-0.3 0.1-0.5 0.3-0.5 0.6v2.4c0 0.3 0.2 0.5 0.5 0.6l2.1 0.7c0.2 0.5 0.4 0.9 0.7 1.3l-0.6 2.2c-0.1 0.3 0 0.6 0.2 0.7l2 1.2c0.2 0.1 0.5 0.1 0.8-0.1l1.3-1.1c0.5 0.2 1 0.4 1.5 0.5l0.7 2.1c0.1 0.3 0.3 0.4 0.6 0.4h2.4c0.3 0 0.5-0.2 0.7-0.4l0.7-2.1c0.5-0.1 1-0.3 1.5-0.5l1.3 1.1c0.2 0.2 0.5 0.2 0.8 0.1l2-1.2c0.3-0.2 0.4-0.5 0.2-0.7l-0.6-2.2c0.3-0.4 0.5-0.9 0.7-1.3L22 13.3c0.3-0.1 0.5-0.3 0.5-0.6v-2.4c0-0.3-0.2-0.5-0.5-0.6l-2.1-0.7c-0.2-0.5-0.4-0.9-0.7-1.3l0.6-2.2c0.1-0.3 0-0.6-0.2-0.7l-2-1.2C17.3 3.4 17 3.5 16.8 3.7l-1.3 1.1c-0.5-0.2-1-0.4-1.5-0.5L13.3 2.4C13.2 2.2 13 2 12.7 2H12zM12 8c2.2 0 4 1.8 4 4s-1.8 4-4 4s-4-1.8-4-4s1.8-4 4-4z"/>
            </motion.svg>

            {/* Small Gear - Always animating */}
            <motion.svg
              className="w-12 h-12 text-[#6D9773]/80 absolute -right-2 -bottom-4"
              viewBox="0 0 24 24"
              fill="currentColor"
              stroke="none"
              animate={{ rotate: 360 }}
              transition={{ duration: 4, repeat: Infinity, ease: "linear", repeatDelay: 0 }}
            >
              <path d="M12 2c-0.3 0-0.5 0.2-0.7 0.4L10 4.1c-0.5 0.1-1 0.3-1.5 0.5L7.2 3.7c-0.2-0.2-0.5-0.2-0.8-0.1l-2 1.2C4.2 5 4.1 5.3 4.2 5.6l0.6 2.2c-0.3 0.4-0.5 0.9-0.7 1.3L2 9.7c-0.3 0.1-0.5 0.3-0.5 0.6v2.4c0 0.3 0.2 0.5 0.5 0.6l2.1 0.7c0.2 0.5 0.4 0.9 0.7 1.3l-0.6 2.2c-0.1 0.3 0 0.6 0.2 0.7l2 1.2c0.2 0.1 0.5 0.1 0.8-0.1l1.3-1.1c0.5 0.2 1 0.4 1.5 0.5l0.7 2.1c0.1 0.3 0.3 0.4 0.6 0.4h2.4c0.3 0 0.5-0.2 0.7-0.4l0.7-2.1c0.5-0.1 1-0.3 1.5-0.5l1.3 1.1c0.2 0.2 0.5 0.2 0.8 0.1l2-1.2c0.3-0.2 0.4-0.5 0.2-0.7l-0.6-2.2c0.3-0.4 0.5-0.9 0.7-1.3L22 13.3c0.3-0.1 0.5-0.3 0.5-0.6v-2.4c0-0.3-0.2-0.5-0.5-0.6l-2.1-0.7c-0.2-0.5-0.4-0.9-0.7-1.3l0.6-2.2c0.1-0.3 0-0.6-0.2-0.7l-2-1.2C17.3 3.4 17 3.5 16.8 3.7l-1.3 1.1c-0.5-0.2-1-0.4-1.5-0.5L13.3 2.4C13.2 2.2 13 2 12.7 2H12zM12 8c2.2 0 4 1.8 4 4s-1.8 4-4 4s-4-1.8-4-4s1.8-4 4-4z"/>
            </motion.svg>
          </div>
          <p className="text-white font-medium mt-4 text-center text-sm">{t('processing')}</p>
        </div>
      )}

      {/* Background Image */}
      <img
        src={getImageUrl(image)}
        alt="Process"
        className={`absolute inset-0 w-full h-full object-cover transition-transform duration-300 transform group-hover:scale-105 ${!imageLoaded ? 'invisible' : ''}`}
        onLoad={handleImageLoad}
        onError={handleImageError}
      />

      {/* Bucket Overlay - Only shown when status is not_paid and no videos are processed */}
      {status === 'not_paid' && processedVideos.length === 0 && imageLoaded && !imageError && (
        <div className="absolute inset-0 w-full h-full bg-[#0C3B2E]/60 backdrop-blur-[1px] flex items-center justify-center">
          <div className="relative flex flex-col items-center justify-center text-center w-full px-4">
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="w-12 h-12 text-[#6D9773]"
            >
              <BucketIcon />
            </motion.div>
            <motion.p 
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-[#6D9773] font-medium mt-3 text-center text-sm max-w-[200px] mx-auto leading-snug tracking-wide"
            >
              {t('addToBucket')}
            </motion.p>
          </div>
        </div>
      )}

      {/* Delete Button */}
      {status?.toLowerCase() !== 'processing' && (
        <button
          onClick={handleDelete}
          className="absolute top-2 right-2 z-10 p-2 bg-white/90 hover:bg-red-500 rounded-full shadow-md hover:shadow-lg transition-all duration-200 group/delete"
          aria-label={t('actions.delete')}
        >
          <svg 
            width="16" 
            height="16" 
            className="text-gray-600 group-hover/delete:text-white transition-colors" 
            fill="currentColor" 
            viewBox="0 0 16 16"
          >
            <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z" />
          </svg>
        </button>
      )}

      {/* Action Badge */}
      {action && action !== 'default' && (
        <div className="absolute top-2 left-2 bg-[#6D9773] bg-opacity-90 text-white px-2 py-1 rounded text-xs backdrop-blur-sm">
          {action} {variety > 1 ? t('variety', { count: variety }) : ''}
        </div>
      )}

      {/* Footer with Status - Only shown when there are processed videos */}
      {status === 'done' && processedVideos?.length > 0 && (
        <div className="absolute bottom-0 left-0 right-0 px-4 py-3 bg-[#6D9773] bg-opacity-90 backdrop-blur-sm">
          <div className="flex justify-between items-center">
            <div className="flex flex-wrap gap-2">
              {processedVideos.map((video, index) => (
                <button 
                  key={video.id}
                  onClick={() => onVideoClick?.(video)}
                  className="bg-white text-green-600 text-xs font-bold px-3 py-2 rounded-md shadow-sm hover:shadow-md transition-shadow duration-200 flex items-center hover:bg-green-50"
                  aria-label={t('actions.viewVersion', { number: index + 1 })}
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {t('version', { number: index + 1 })}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Card;